import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BookOpen, Save, Loader2, Sparkles } from "lucide-react";

interface ProjectKnowledgePanelProps {
  projectId: string | null;
}

const PLACEHOLDER_TEXT = `Define your project context here. This will be included in every AI prompt.

Examples:
• Brand: "We're a fintech startup. Use professional, trustworthy language. Primary color is emerald green (#10b981)."
• Stack: "Always use Zustand for state management. Prefer server actions over API routes."
• Conventions: "Use camelCase for variables, PascalCase for components. Add JSDoc comments to all exported functions."
• Tone: "Keep copy concise and action-oriented. Avoid corporate jargon."`;

export function ProjectKnowledgePanel({ projectId }: ProjectKnowledgePanelProps) {
  const [knowledge, setKnowledge] = useState("");
  const [savedKnowledge, setSavedKnowledge] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load existing knowledge
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const loadKnowledge = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("code_projects")
          .select("project_knowledge")
          .eq("id", projectId)
          .single();

        if (error) {
          console.warn("[ProjectKnowledgePanel] Failed to load:", error.message);
        } else if (data?.project_knowledge) {
          setKnowledge(data.project_knowledge);
          setSavedKnowledge(data.project_knowledge);
        }
      } catch (err) {
        console.error("[ProjectKnowledgePanel] Load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadKnowledge();
  }, [projectId]);

  const handleSave = useCallback(async () => {
    if (!projectId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("code_projects")
        .update({ project_knowledge: knowledge.trim() || null })
        .eq("id", projectId);

      if (error) {
        throw error;
      }

      setSavedKnowledge(knowledge.trim());
      toast.success("Project knowledge saved");
    } catch (err) {
      console.error("[ProjectKnowledgePanel] Save error:", err);
      toast.error("Failed to save project knowledge");
    } finally {
      setSaving(false);
    }
  }, [projectId, knowledge]);

  const hasChanges = knowledge.trim() !== savedKnowledge;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#0a0a12]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-indigo-400" />
          <span className="text-sm font-medium text-zinc-200">Project Knowledge</span>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="h-7 px-3 text-xs bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
          ) : (
            <Save className="h-3 w-3 mr-1.5" />
          )}
          Save
        </Button>
      </div>

      {/* Info */}
      <div className="px-4 py-3 border-b border-zinc-800/50 bg-indigo-500/5">
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-indigo-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-zinc-400 leading-relaxed">
            Define your brand, stack preferences, and coding conventions. This context is included in every AI build and edit request.
          </p>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 p-4 min-h-0">
        <Textarea
          value={knowledge}
          onChange={(e) => setKnowledge(e.target.value)}
          placeholder={PLACEHOLDER_TEXT}
          className="h-full resize-none bg-zinc-900/50 border-zinc-800 text-zinc-200 placeholder:text-zinc-600 text-sm font-mono leading-relaxed focus:border-indigo-500/50 focus:ring-indigo-500/20"
        />
      </div>

      {/* Footer */}
      {hasChanges && (
        <div className="px-4 py-2 border-t border-zinc-800/50 bg-amber-500/5">
          <p className="text-xs text-amber-400">Unsaved changes</p>
        </div>
      )}
    </div>
  );
}
